import { showError, showSuccess } from "common/ToastNotifications";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import { MessageBox } from "components/atoms/MessageBox";
import ApiKeySecretModal from "components/molecules/ApiKeySecretModal";
import OkCancelConfirmationBox from "components/molecules/OkCancelConfirmationBox";
import ProhibitedArea from "components/molecules/ProhibitedArea";
import ApiKeyList from "components/organisms/ApiKeyList";
import ManageApiKey from "components/organisms/ManageApiKey";
import AppPaths from "constants/appPaths";
import flatten from "flat";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import actions from "store/actions";
import ApiKeyAudit from "./ApiKeyAudit";

const ApiKeys = (props) => {
    const { action, apiKeyId } = props;
    const [busy, setBusy] = useState(false);
    const [busyMessage, setBusyMessage] = useState("");
    const [fetchingPermission, setFetchingPermission] = useState(true);
    const [fetchingApiKeys, setFetchingApiKeys] = useState(false);
    const [fetchingApiKey, setFetchingApiKey] = useState(false);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [manageApiKeyErrors, setManageApiKeyErrors] = useState({});
    // The plaintext key is displayed exactly once, right after create.
    const [secretModal, setSecretModal] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, apiKey: null, message: "" });
    const [activateDeactivate, setActivateDeactivate] = useState(null);

    const history = useHistory();
    const { tenant, configurationItem } = useParams();
    const prevProps = useRef(props);

    const listPath = useMemo(
        () => AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", tenant).replace(":configurationItem", configurationItem.toLowerCase()),
        [tenant, configurationItem],
    );

    const itemPath = useCallback(
        (id, itemAction) =>
            AppPaths.TENANT_CONFIGURATION_ITEM_ID_ACTION.replace(":tenant", tenant)
                .replace(":configurationItem", configurationItem.toLowerCase())
                .replace(":itemId", id)
                .replace(":itemAction", itemAction),
        [tenant, configurationItem],
    );

    const goToList = useCallback(() => history.push(listPath), [history, listPath]);

    useEffect(() => {
        props.getApiKeyPermission();
        props.getUsers();
        if (!action) {
            setBusy(true);
            setBusyMessage("Getting API keys...");
            setFetchingApiKeys(true);
            props.getApiKeys();
        } else {
            if (apiKeyId) {
                props.getApiKey(apiKeyId);
            }
            if (action === "view" || action === "edit") {
                setBusy(true);
                setBusyMessage("Getting API key...");
                setFetchingApiKey(true);
            } else if (action === "history") {
                setShowHistory(false);
                setBusy(true);
                setFetchingApiKey(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, apiKeyId]);

    const apiKeyName = useMemo(() => {
        let name = apiKeyId;
        if (props.apiKeyListResult.Items && props.apiKeyListResult.Items.length > 0) {
            let apiKey = props.apiKeyListResult.Items.find((item) => item.apiKeyId === apiKeyId);
            if (apiKey) {
                name = apiKey.name;
            }
        } else if (props.apiKey && props.apiKey.apiKeyId === apiKeyId) {
            name = props.apiKey.name;
        }
        return name;
    }, [props.apiKeyListResult, props.apiKey, apiKeyId]);

    useEffect(() => {
        if (props.showSubBreadCrumbNavigationItems) {
            let breadCrumbItems = [
                {
                    route: AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", tenant.toLowerCase()).replace(":configurationItem", configurationItem.toLowerCase()),
                    text: "API Keys",
                },
            ];
            if (action) {
                switch (action.toLowerCase()) {
                    case "create":
                        breadCrumbItems.push({ route: "", text: "New API Key" });
                        break;
                    case "view":
                    case "edit":
                        breadCrumbItems.push({ route: "", text: apiKeyName });
                        break;
                    case "history":
                        breadCrumbItems.push({ route: "", text: apiKeyName }, { route: "", text: "History" });
                        break;
                    default:
                        break;
                }
            }
            props.showSubBreadCrumbNavigationItems(breadCrumbItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, apiKeyId, apiKeyName]);

    useEffect(() => {
        const previous = prevProps.current;

        if (props.getApiKeysResult && props.getApiKeysResult !== previous.getApiKeysResult) {
            if (!props.getApiKeysResult.success) {
                if (props.getApiKeysResult.code === "PERMISSION_DENIED") {
                    setPermissionDenied(true);
                } else {
                    showError("Unable to get API keys at this moment.");
                }
            }
            setBusy(false);
            setBusyMessage("");
            setFetchingApiKeys(false);
        }

        if (props.createApiKeyResult && props.createApiKeyResult !== previous.createApiKeyResult) {
            if (!props.createApiKeyResult.success) {
                showError(props.createApiKeyResult.message || "API key cannot be created at this moment.");
                setManageApiKeyErrors(props.createApiKeyResult.fields);
            } else {
                showSuccess("API key created successfully.");
                setSecretModal({ apiKey: props.createdApiKey });
            }
            setBusy(false);
            setBusyMessage("");
        }

        if (props.getApiKeyResult && props.getApiKeyResult !== previous.getApiKeyResult) {
            if (!props.getApiKeyResult.success) {
                if (props.getApiKeyResult.code === "PERMISSION_DENIED") {
                    setPermissionDenied(true);
                } else {
                    showError("Unable to get API key.");
                }
            } else if (action === "history" && props.apiKey) {
                setShowHistory(true);
            }
            setBusy(false);
            setBusyMessage("");
            setFetchingApiKey(false);
        }

        if (props.updateApiKeyResult && props.updateApiKeyResult !== previous.updateApiKeyResult) {
            if (!props.updateApiKeyResult.success) {
                if (props.updateApiKeyResult.code === "PERMISSION_DENIED") {
                    setPermissionDenied(true);
                } else {
                    showError(props.updateApiKeyResult.message || "Unable to update API key.");
                    setManageApiKeyErrors(props.updateApiKeyResult.fields);
                }
                setBusy(false);
                setActivateDeactivate(null);
            } else if (activateDeactivate) {
                showSuccess(`API key ${activateDeactivate.active ? "activated" : "deactivated"} successfully.`);
                setBusy(false);
                setActivateDeactivate(null);
            } else {
                setBusyMessage("API key updated. Please wait...");
                showSuccess("API key updated successfully.", () => {
                    goToList();
                });
            }
        }

        if (props.deleteApiKeyResult && props.deleteApiKeyResult !== previous.deleteApiKeyResult) {
            if (!props.deleteApiKeyResult.success) {
                showError(props.deleteApiKeyResult.message || "API key cannot be deleted.");
            } else {
                showSuccess("API key deleted successfully.");
            }
            setBusy(false);
        }

        if (props.getApiKeyPermissionResult && props.getApiKeyPermissionResult !== previous.getApiKeyPermissionResult) {
            if (!props.getApiKeyPermissionResult.success || !props.permission || !props.permission.canView) {
                setPermissionDenied(true);
            }
            setFetchingPermission(false);
        }

        prevProps.current = props;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.getApiKeysResult,
        props.createApiKeyResult,
        props.getApiKeyResult,
        props.updateApiKeyResult,
        props.deleteApiKeyResult,
        props.getApiKeyPermissionResult,
    ]);

    const showAddApiKey = useCallback(() => {
        history.push(
            AppPaths.TENANT_CONFIGURATION_ITEM_ACTION.replace(":tenant", tenant).replace(":configurationItem", configurationItem.toLowerCase()).replace(":itemAction", "create"),
        );
    }, [history, tenant, configurationItem]);

    const showEditApiKey = useCallback((id) => history.push(itemPath(id, "edit")), [history, itemPath]);
    const showViewApiKey = useCallback((id) => history.push(itemPath(id, "view")), [history, itemPath]);
    const showApiKeyHistory = useCallback((id) => history.push(itemPath(id, "history")), [history, itemPath]);

    const createApiKey = useCallback(
        (apiKey) => {
            setBusy(true);
            setBusyMessage("Creating API key...");
            props.createApiKey(apiKey);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const updateApiKey = useCallback(
        (id, updatedApiKey) => {
            if (_.isEmpty(updatedApiKey) === false) {
                setBusy(true);
                setBusyMessage("Updating API key...");
                props.updateApiKey(id, updatedApiKey);
            } else {
                goToList();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [goToList],
    );

    const onActivateDeactivateApiKey = useCallback(
        (apiKey, active) => {
            setActivateDeactivate({ active });
            setBusy(true);
            setBusyMessage(active ? "Activating API key..." : "Deactivating API key...");
            props.updateApiKey(apiKey.apiKeyId, { version: apiKey.version, active });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onApiKeyValuesChanged = useCallback(
        (changedValues) => {
            if (_.isEmpty(manageApiKeyErrors) === false) {
                let errors = { ...manageApiKeyErrors };
                let flatObject = flatten(changedValues);
                for (let key in flatObject) {
                    delete errors[key];
                }
                setManageApiKeyErrors(errors);
            }
        },
        [manageApiKeyErrors],
    );

    const onSecretModalClosed = useCallback(() => {
        setSecretModal(null);
        props.clearApiKeySecret();
        goToList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goToList]);

    const showDeleteApiKeyConfirmation = useCallback((apiKey) => {
        setDeleteConfirmation({
            show: true,
            apiKey,
            message: `Are you sure you want to delete the API key '${apiKey.name}'? Any integration using it will stop working immediately.`,
        });
    }, []);

    const onDeleteApiKeyCancel = useCallback(() => setDeleteConfirmation({ show: false, apiKey: null, message: "" }), []);

    const onDeleteApiKeyConfirmed = useCallback(() => {
        setBusy(true);
        setBusyMessage("Deleting API key...");
        props.deleteApiKey(deleteConfirmation.apiKey.apiKeyId);
        onDeleteApiKeyCancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteConfirmation, onDeleteApiKeyCancel]);

    const isBusy = () => {
        let result = busy || fetchingApiKeys || fetchingPermission;
        if (action === "view" || action === "edit") {
            result = result || fetchingApiKey;
        }
        return result;
    };

    const getApiKeyComponent = () => {
        if (permissionDenied) {
            return <ProhibitedArea></ProhibitedArea>;
        }
        if (!action) {
            return (
                <>
                    <OkCancelConfirmationBox
                        show={deleteConfirmation.show}
                        message={deleteConfirmation.message}
                        onCancel={onDeleteApiKeyCancel}
                        onOk={onDeleteApiKeyConfirmed}
                    ></OkCancelConfirmationBox>
                    {!fetchingApiKeys ? (
                        <ApiKeyList
                            permission={props.permission}
                            apiKeyList={props.apiKeyListResult.Items}
                            userList={props.userList}
                            onAddApiKey={showAddApiKey}
                            onViewApiKey={showViewApiKey}
                            onEditApiKey={showEditApiKey}
                            onDeleteApiKey={showDeleteApiKeyConfirmation}
                            onActivateDeactivateApiKey={onActivateDeactivateApiKey}
                            onViewApiKeyHistory={showApiKeyHistory}
                        />
                    ) : (
                        <></>
                    )}
                </>
            );
        }
        switch (action.toLowerCase()) {
            case "create":
                return (
                    <ManageApiKey
                        permission={props.permission}
                        action={action}
                        onSave={createApiKey}
                        onCancel={goToList}
                        formErrors={manageApiKeyErrors}
                        onValuesChanged={onApiKeyValuesChanged}
                    ></ManageApiKey>
                );
            case "view":
            case "edit":
                return !fetchingApiKey ? (
                    props.apiKey ? (
                        <ManageApiKey
                            permission={props.permission}
                            action={action}
                            apiKey={props.apiKey}
                            userList={props.userList}
                            onSave={updateApiKey}
                            onCancel={goToList}
                            formErrors={manageApiKeyErrors}
                            onValuesChanged={onApiKeyValuesChanged}
                        ></ManageApiKey>
                    ) : props.getApiKeyResult.success ? (
                        <MessageBox floating size="big" header="API key not found.">
                            <p>The API key you are looking for does not exist.</p>
                        </MessageBox>
                    ) : (
                        <></>
                    )
                ) : (
                    <></>
                );
            case "history":
                if (fetchingApiKey) {
                    return <></>;
                }
                // A key that was deleted or never existed reads back as null, so guard before the audit view.
                if (!props.apiKey) {
                    return props.getApiKeyResult.success ? (
                        <MessageBox floating size="big" header="API key not found.">
                            <p>The API key you are looking for does not exist.</p>
                        </MessageBox>
                    ) : (
                        <></>
                    );
                }
                return showHistory ? <ApiKeyAudit apiKey={props.apiKey} onClose={goToList}></ApiKeyAudit> : <></>;
            default:
                return <ProhibitedArea></ProhibitedArea>;
        }
    };

    return (
        <>
            {secretModal && secretModal.apiKey ? (
                <ApiKeySecretModal show={true} apiKey={secretModal.apiKey} onClose={onSecretModalClosed}></ApiKeySecretModal>
            ) : (
                <></>
            )}
            <LoadingOverlay busy={isBusy()} spinner message={busyMessage || "Please wait..."}></LoadingOverlay>
            {!fetchingPermission ? getApiKeyComponent() : <></>}
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        apiKeyListResult: state.apiKeys.apiKeyListResult,
        getApiKeysResult: state.apiKeys.getApiKeysResult,
        createApiKeyResult: state.apiKeys.createApiKeyResult,
        createdApiKey: state.apiKeys.createdApiKey,
        getApiKeyResult: state.apiKeys.getApiKeyResult,
        apiKey: state.apiKeys.apiKey,
        updateApiKeyResult: state.apiKeys.updateApiKeyResult,
        deleteApiKeyResult: state.apiKeys.deleteApiKeyResult,
        getApiKeyPermissionResult: state.apiKeys.getApiKeyPermissionResult,
        permission: state.apiKeys.userApiKeyPermission,
        userList: state.users.userList,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getApiKeyPermission: () => dispatch(actions.getApiKeyPermissionRequest()),
        getApiKeys: () => dispatch(actions.getApiKeysRequest()),
        getUsers: () => dispatch(actions.getUsersRequest()),
        createApiKey: (apiKey) => dispatch(actions.createApiKeyRequest(apiKey)),
        getApiKey: (apiKeyId) => dispatch(actions.getApiKeyRequest(apiKeyId)),
        updateApiKey: (apiKeyId, updatedApiKey) => dispatch(actions.updateApiKeyRequest(apiKeyId, updatedApiKey)),
        deleteApiKey: (apiKeyId) => dispatch(actions.deleteApiKeyRequest(apiKeyId)),
        clearApiKeySecret: () => dispatch(actions.clearApiKeySecret()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiKeys);
